import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  Loader2,
  AlertCircle,
  Mail,
  LogOut
} from 'lucide-react';
import { auth, db } from './config/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { App as CapApp } from '@capacitor/app';
import { AdMobManager } from './utils/AdMobManager';
import LoginScreen from './components/auth/LoginScreen';
import RoleSelectionScreen from './components/auth/RoleSelectionScreen';
import ErrorBoundary from './components/ui/ErrorBoundary';
import OfflineIndicator from './components/ui/OfflineIndicator';
import SyncIndicator from './components/ui/SyncIndicator';
import SkipLink from './components/ui/SkipLink';
import { LiveAnnouncerProvider } from './components/ui/LiveAnnouncer';
import { useAutoSync } from './hooks/useAutoSync';
import { SyncQueueProvider } from './hooks/useSyncQueue.jsx';
import { useUserData } from './hooks/useUserData';
import { GlobalErrorProvider } from './context/GlobalErrorContext';
import { AlertProvider, useAlert } from './context/AlertContext';
import ErrorModal from './components/ui/ErrorModal';
import { TourProvider } from './context/TourContext';
import { ThemeProvider } from './context/ThemeContext';
import { UnitProvider } from './context/UnitContext';
import CookieConsent from './components/ui/CookieConsent';

import logger from './utils/logger';



import Layout from './components/layout/Layout';
import SEOHead from './components/layout/SEOHead';


// Marketing/Public pages (Lazy loaded except LandingPage for LCP)
import LandingPage from './components/marketing/LandingPage';
import ForgotPasswordScreen from './components/auth/ForgotPasswordScreen';
const TermsOfService = lazy(() => import('./components/marketing/TermsOfService'));
const CookiePolicy = lazy(() => import('./components/marketing/CookiePolicy'));
const PrivacyPolicy = lazy(() => import('./components/marketing/PrivacyPolicy'));
const AboutPage = lazy(() => import('./components/marketing/AboutPage'));
const ContactPage = lazy(() => import('./components/marketing/ContactPage'));
const ResourcesPage = lazy(() => import('./components/marketing/ResourcesPage'));
const ResourceArticle = lazy(() => import('./components/marketing/ResourceArticle'));
const DataDeletionView = lazy(() => import('./components/public/DataDeletionView'));
const NotFoundPage = lazy(() => import('./components/public/NotFoundPage'));



// SECURITY: Database access removed from window object
// Previously exposed db, auth, and firestore to window for debugging
// This was a critical security vulnerability - removed completely

// Lazy load heavy components
const TrainerDashboard = lazy(() => import('./components/dashboard/TrainerDashboard'));
const AnalyticsView = lazy(() => import('./components/dashboard/AnalyticsView'));
const RankingView = lazy(() => import('./components/dashboard/RankingView'));
const FeedView = lazy(() => import('./components/social/FeedView'));
const FriendsView = lazy(() => import('./components/dashboard/FriendsView'));
const TrainingManager = lazy(() => import('./components/dashboard/TrainingManager'));
const CoachView = lazy(() => import('./components/dashboard/CoachView'));
const ProfileView = lazy(() => import('./components/dashboard/ProfileView'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const OneRepMaxTool = lazy(() => import('./components/tools/OneRepMaxTool'));
const IPFPointsTool = lazy(() => import('./components/tools/IPFPointsTool'));
const PlateCalculatorTool = lazy(() => import('./components/tools/PlateCalculatorTool'));
const MeetSimulator = lazy(() => import('./components/tools/MeetSimulator'));
const MacroCalculatorTool = lazy(() => import('./components/tools/MacroCalculatorTool'));
const AnatomyViewer = lazy(() => import('./components/tools/anatomy/AnatomyViewer'));
const ToolsDirectory = lazy(() => import('./components/tools/ToolsDirectory'));
const ExerciseDirectory = lazy(() => import('./components/marketing/ExerciseDirectory'));
const ExerciseDetail = lazy(() => import('./components/marketing/ExerciseDetail'));
const SharedRoutineViewer = lazy(() => import('./components/public/SharedRoutineViewer'));
const Competitions = lazy(() => import('./components/competitions/Competitions'));
const CompetitionDetail = lazy(() => import('./components/competitions/CompetitionDetail'));
const JoinByCode = lazy(() => import('./components/competitions/JoinByCode'));


// Suspense Loading Fallback
const LoadingFallback = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center h-screen bg-black">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="animate-spin text-red-500" size={40} />
        <p className="text-neutral-500 font-bold uppercase text-sm tracking-wider">{t('routine_editor.loading')}</p>
      </div>
    </div>
  );
};

export default function App() {
  return (
    <GlobalErrorProvider>
      <AlertProvider>
        <ThemeProvider>
          <UnitProvider>
            <BrowserRouter>
              <AppContent />
              <CookieConsent />
            </BrowserRouter>
          </UnitProvider>
        </ThemeProvider>
      </AlertProvider>
    </GlobalErrorProvider>
  );
}

function AppContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useAlert();
  const {
    user,
    profile,
    role,
    roleLoading,
    loading,
    isProfileComplete,
    markProfileComplete,
    clearPendingDeletion,
    pendingDeletion,
    updateRole,
    userLoadError
  } = useUserData();

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize Global Auto-Sync
  useAutoSync();

  // Initialize AdMob and App Open Ad listener
  useEffect(() => {
    AdMobManager.init();

    const resumeListener = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        AdMobManager.showAppOpen();
      }
    });

    return () => {
      resumeListener.then(l => l.remove());
    };
  }, []);

  // Sync activeTab with URL
  const [activeTab, setActiveTab] = useState(() => {
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const importId = urlParams.get('importRoutine');
    if (importId) {
      localStorage.setItem('pendingImportRoutine', importId);
    }

    if (path === '/privacy') return 'privacy';
    if (path === '/admin') return 'admin';
    // Remove leading slash
    const tab = path.substring(1);
    // Simplify: default to home if empty, else use path segment
    return tab || 'home';
  });

  useEffect(() => {
    const path = location.pathname;
    let newTab = 'home';

    if (path === '/') newTab = 'home';
    else if (path === '/privacy') newTab = 'privacy';
    else if (path === '/admin') newTab = 'admin';
    else {
      // Extract first segment: /profile -> profile
      const segment = path.split('/')[1];
      if (segment) newTab = segment;
    }

    setActiveTab(newTab);

    // Reset specific view states on route change
    if (newTab === 'home' && role === 'trainer') {
      setViewingClientId(null);
      setViewingClientName(null);
      setViewingClientStatsId(null);
      setViewingClientStatsName(null);
    }
    if (newTab === 'friends') {
      setViewingFriendId(null);
      setViewingFriendName(null);
    }

    window.scrollTo(0, 0);
  }, [location.pathname, role]);

  const [viewingClientId, setViewingClientId] = useState(null);
  const [viewingClientName, setViewingClientName] = useState(null);
  const [viewingClientStatsId, setViewingClientStatsId] = useState(null);
  const [viewingClientStatsName, setViewingClientStatsName] = useState(null);
  const [viewingFriendId, setViewingFriendId] = useState(null);
  const [viewingFriendName, setViewingFriendName] = useState(null);

  // Effect to handle profile completion redirection
  useEffect(() => {
    if (user && !loading && isProfileComplete === false) {
      // First-time users or users with incomplete profiles must complete their profile
      if (location.pathname !== '/profile') {
        const currentPath = encodeURIComponent(location.pathname + location.search);
        navigate(`/profile?redirectTo=${currentPath}`);
      }
    }
  }, [user, loading, isProfileComplete, location.pathname]);

  // Effect to redirect to home (rutinas) after login when profile is complete
  useEffect(() => {
    if (user && !loading && isProfileComplete === true) {
      // If user is on auth pages after login, redirect to app home or redirectTo path
      if (location.pathname === '/login' || location.pathname === '/register') {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirectTo');

        // Security check: ensure it's a relative path and not an auth page
        if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('/login') && !redirectTo.startsWith('/register')) {
          navigate(redirectTo);
        } else {
          navigate('/');
        }
      }
    }
  }, [user, loading, isProfileComplete, location.search]);


  // Reactivate account
  const reactivateAccount = async () => {
    if (!user?.uid) return;

    const confirmReactivate = await showConfirm(
      '¿Quieres reactivar tu cuenta?\n\nEsto cancelará la eliminación programada y restaurará tu cuenta completamente.',
      { title: 'Reactivar Cuenta' }
    );

    if (!confirmReactivate) return;

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        accountStatus: 'active',
        deletionScheduledAt: null,
        deletionExecutionAt: null
      });

      clearPendingDeletion();
      await showAlert('¡Tu cuenta ha sido reactivada correctamente!', { type: 'success' });
      logger.info('Account reactivated', { userId: user.uid });
    } catch (error) {
      console.error('Error reactivating account:', error);
      await showAlert('Error al reactivar la cuenta. Inténtalo de nuevo.', { type: 'error' });
    }
  };

  // Callback when profile is saved successfully
  const handleProfileSaved = () => {
    markProfileComplete();
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get('redirectTo');
    if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('/profile')) {
      navigate(redirectTo);
    } else {
      navigate('/');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    navigate('/');
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleNavigate = (tabId) => {
    if (!isProfileComplete && tabId !== 'profile') return;

    // Just navigate, let the effect handle state update
    navigate(tabId === 'home' ? '/' : `/${tabId}`);
  };

  const mergedUser = React.useMemo(() => {
    if (!user) return null;
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      ...(profile || {})
    };
  }, [user, profile]);

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        if (role === 'athlete') {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <TrainingManager user={mergedUser} profile={profile} role="athlete" />
            </Suspense>
          );
        } else {
          if (viewingClientId) {
            return (
              <Suspense fallback={<LoadingFallback />}>
                <TrainingManager
                  user={mergedUser}
                  profile={profile}
                  role="trainer"
                  clientId={viewingClientId}
                  clientName={viewingClientName}
                  onBack={() => {
                    setViewingClientId(null);
                    setViewingClientName(null);
                  }}
                />
              </Suspense>
            );
          } else if (viewingClientStatsId) {
            return (
              <Suspense fallback={<LoadingFallback />}>
                <AnalyticsView
                  user={mergedUser}
                  userId={viewingClientStatsId}
                  userName={viewingClientStatsName}
                  onBack={() => {
                    setViewingClientStatsId(null);
                    setViewingClientStatsName(null);
                  }}
                />
              </Suspense>
            );
          } else {
            return (
              <Suspense fallback={<LoadingFallback />}>
                <TrainerDashboard
                  user={mergedUser}
                  onSelectClient={(client) => {
                    setViewingClientId(client.id);
                    setViewingClientName(client.name);
                  }}
                  onViewStats={(client) => {
                    setViewingClientStatsId(client.id);
                    setViewingClientStatsName(client.name);
                  }}
                />
              </Suspense>
            );
          }
        }
      case 'analytics':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <AnalyticsView user={mergedUser} />
          </Suspense>
        );
      case 'ranking':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <RankingView user={mergedUser} />
          </Suspense>
        );
      case 'feed':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <FeedView user={mergedUser} />
          </Suspense>
        );
      case 'friends':
        if (viewingFriendId) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <ProfileView
                user={{ uid: viewingFriendId, displayName: viewingFriendName }}
                role="athlete"
                readOnly={true}
                onBack={() => { setViewingFriendId(null); setViewingFriendName(null); }}
              />
            </Suspense>
          );
        }
        return (
          <Suspense fallback={<LoadingFallback />}>
            <FriendsView user={user} profile={profile} onViewFriend={(friend) => { setViewingFriendId(friend.id); setViewingFriendName(friend.name); }} />
          </Suspense>
        );
      case 'coach':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CoachView user={user} />
          </Suspense>
        );
      case 'profile':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ProfileView user={user} profile={profile} role={role} onProfileSaved={handleProfileSaved} />
          </Suspense>
        );
      case 'privacy':
        return <PrivacyPolicy />;
      case 'admin':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <AdminDashboard />
          </Suspense>
        );
      case 'exercises':
        if (window.location.pathname === '/exercises' || window.location.pathname === '/exercises/') {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <ExerciseDirectory />
            </Suspense>
          );
        }
        if (window.location.pathname.startsWith('/exercises/')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <ExerciseDetail embedded={true} />
            </Suspense>
          );
        }
        return <NotFoundPage />;
      case 'tools':
        if (window.location.pathname === '/tools' || window.location.pathname === '/tools/') {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <ToolsDirectory embedded={true} />
            </Suspense>
          );
        } else if (window.location.pathname.includes('/tools/1rm-calculator')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <OneRepMaxTool embedded={true} />
            </Suspense>
          );
        } else if (window.location.pathname.includes('/tools/ipf-calculator')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <IPFPointsTool embedded={true} />
            </Suspense>
          );
        } else if (window.location.pathname.includes('/tools/competition-hub')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <MeetSimulator embedded={true} />
            </Suspense>
          );
        } else if (window.location.pathname.includes('/tools/plate-calculator')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <PlateCalculatorTool embedded={true} />
            </Suspense>
          );
        } else if (window.location.pathname.includes('/tools/anatomy')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <AnatomyViewer />
            </Suspense>
          );
        }
        return <NotFoundPage />;
      case 'competitions':
        if (window.location.pathname.startsWith('/competitions/')) {
          return (
            <Suspense fallback={<LoadingFallback />}>
              <CompetitionDetail />
            </Suspense>
          );
        }
        return (
          <Suspense fallback={<LoadingFallback />}>
            <Competitions />
          </Suspense>
        );
      case 'join':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <JoinByCode />
          </Suspense>
        );
      default:
        return null;
    }
  };

  // PUBLIC MARKETING ROUTES - Render immediately without waiting for auth/profile loading
  // This allows browsing marketing pages while authenticated without any delays or redirects
  const publicMarketingPaths = ['/tools', '/tools/macro-calculator', '/tools/anatomy', '/competitions', '/join', '/exercises', '/about', '/contact', '/resources', '/landing', '/privacy', '/terms', '/cookies', '/data-deletion'];
  const isPublicMarketingRoute = publicMarketingPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  if (isPublicMarketingRoute && location.pathname !== '/app') {
    const path = location.pathname;

    if (path === '/tools/1rm-calculator') return <Suspense fallback={<LoadingFallback />}><OneRepMaxTool /></Suspense>;
    if (path === '/tools/competition-hub') return <Suspense fallback={<LoadingFallback />}><MeetSimulator /></Suspense>;
    if (path === '/tools/ipf-calculator') return <Suspense fallback={<LoadingFallback />}><IPFPointsTool /></Suspense>;
    if (path === '/tools/plate-calculator') return <Suspense fallback={<LoadingFallback />}><PlateCalculatorTool /></Suspense>;
    if (path === '/tools/macro-calculator') return <Suspense fallback={<LoadingFallback />}><MacroCalculatorTool /></Suspense>;
    if (path === '/tools/anatomy') return <Suspense fallback={<LoadingFallback />}><AnatomyViewer /></Suspense>;
    if (path === '/tools') return <Suspense fallback={<LoadingFallback />}><ToolsDirectory /></Suspense>;
    if (path.startsWith('/competitions/')) return <Suspense fallback={<LoadingFallback />}><CompetitionDetail /></Suspense>;
    if (path === '/competitions') return <Suspense fallback={<LoadingFallback />}><Competitions /></Suspense>;
    if (path.startsWith('/join/')) return <Suspense fallback={<LoadingFallback />}><JoinByCode /></Suspense>;
    if (path === '/landing') return <LandingPage />;
    if (path === '/exercises') return <Suspense fallback={<LoadingFallback />}><ExerciseDirectory /></Suspense>;
    if (path.startsWith('/exercises/')) return <Suspense fallback={<LoadingFallback />}><ExerciseDetail /></Suspense>;
    if (path === '/about') return <AboutPage />;
    if (path === '/contact') return <ContactPage />;
    if (path === '/resources') return <ResourcesPage />;
    if (path.startsWith('/resources/')) return <ResourceArticle />;
    if (path === '/privacy') return <PrivacyPolicy />;
    if (path === '/terms') return <TermsOfService />;
    if (path === '/cookies') return <CookiePolicy />;
    if (path === '/data-deletion') return <DataDeletionView />;
    if (path === '/forgot-password') return <ForgotPasswordScreen />;

    // Catch-all for sub-paths of marketing routes that weren't handled
    return <NotFoundPage />;
  }

  if (loading) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 relative z-50">
      <div className="relative">
        <div className="absolute inset-0 bg-red-600 blur-xl opacity-20 animate-pulse"></div>
        <Activity size={48} className="text-red-600 animate-bounce relative z-10" />
      </div>
      <p className="text-red-600 font-black italic tracking-widest animate-pulse text-sm">{t('routine_editor.loading_system')}</p>

      {/* Emergency Reset Button if stuck */}
      <button
        onClick={() => {
          localStorage.clear();
          window.location.reload();
        }}
        className="absolute bottom-10 text-neutral-600 text-xs underline hover:text-red-500"
      >
        {t('routine_editor.reset_app_error')}
      </button>
    </div>
  );

  if (!user) {
    const path = window.location.pathname;

    // Public routes (no authentication required)
    if (path === '/') return <LandingPage />;
    if (path === '/privacy') return <PrivacyPolicy />;
    if (path === '/terms') return <TermsOfService />;
    if (path === '/cookies') return <CookiePolicy />;
    if (path === '/about') return <AboutPage />;
    if (path === '/contact') return <ContactPage />;
    if (path === '/resources') return <ResourcesPage />;
    if (path.startsWith('/resources/')) return <ResourceArticle />;
    if (path === '/data-deletion') return <DataDeletionView />;
    if (path === '/exercises') return (
      <Suspense fallback={<LoadingFallback />}>
        <ExerciseDirectory />
      </Suspense>
    );
    if (path.startsWith('/exercises/')) return (
      <Suspense fallback={<LoadingFallback />}>
        <ExerciseDetail />
      </Suspense>
    );
    if (path === '/tools/1rm-calculator') return (
      <Suspense fallback={<LoadingFallback />}>
        <OneRepMaxTool />
      </Suspense>
    );
    if (path === '/tools/competition-hub') return (
      <Suspense fallback={<LoadingFallback />}>
        <MeetSimulator />
      </Suspense>
    );
    if (path === '/tools/ipf-calculator') return (
      <Suspense fallback={<LoadingFallback />}>
        <IPFPointsTool />
      </Suspense>
    );
    if (path === '/tools/plate-calculator') return (
      <Suspense fallback={<LoadingFallback />}>
        <PlateCalculatorTool />
      </Suspense>
    );
    if (path === '/tools/macro-calculator') return (
      <Suspense fallback={<LoadingFallback />}>
        <MacroCalculatorTool />
      </Suspense>
    );
    if (path === '/tools/anatomy') return (
      <Suspense fallback={<LoadingFallback />}>
        <AnatomyViewer />
      </Suspense>
    );
    if (path === '/tools') return (
      <Suspense fallback={<LoadingFallback />}>
        <ToolsDirectory />
      </Suspense>
    );
    if (path === '/competitions') return (
      <Suspense fallback={<LoadingFallback />}>
        <Competitions />
      </Suspense>
    );
    if (path.startsWith('/competitions/')) return (
      <Suspense fallback={<LoadingFallback />}>
        <CompetitionDetail />
      </Suspense>
    );
    if (path.startsWith('/join/')) return (
      <Suspense fallback={<LoadingFallback />}>
        <JoinByCode />
      </Suspense>
    );

    if (path === '/forgot-password') return <ForgotPasswordScreen />;

    // Login variants
    if (path === '/acceso-demo' || path === '/login-admin') {
      return <LoginSimple />;
    }

    // Default to login for any other route
    // Default to login for any other route that requires auth
    const protectedRoutes = ['/profile', '/analytics', '/ranking', '/feed', '/friends', '/coach', '/admin', '/competitions'];
    const isProtectedRoute = protectedRoutes.some(route => path.startsWith(route));

    if (path === '/login' || path === '/register' || path.startsWith('/dashboard') || isProtectedRoute) {
      return <LoginScreen />;
    }

    // For unknown routes, redirect to 404
    if (path.startsWith('/routine/')) {
      const routineId = path.split('/')[2];
      return (
        <Suspense fallback={<LoadingFallback />}>
          <SharedRoutineViewer routineId={routineId} />
        </Suspense>
      );
    }

    return <NotFoundPage />;
  }

  // Public marketing routes already handled above, before loading state
  if (userLoadError) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 px-4 text-center">
        <AlertCircle size={48} className="text-red-600" />
        <h2 className="text-xl font-black text-white uppercase tracking-tight">{t('auth.connection_error_title')}</h2>
        <p className="text-neutral-400 text-sm max-w-md">
          {t('auth.connection_error_desc')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-neutral-800 hover:bg-neutral-700 text-white font-bold py-2 px-6 rounded-lg uppercase tracking-wider transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }
  // Show loading while role is being fetched
  if (roleLoading) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 relative z-50">
      <div className="relative">
        <div className="absolute inset-0 bg-red-600 blur-xl opacity-20 animate-pulse"></div>
        <Activity size={48} className="text-red-600 animate-bounce relative z-10" />
      </div>
      <p className="text-red-600 font-black italic tracking-widest animate-pulse text-sm">{t('auth.loading_profile')}</p>
    </div>
  );

  // Only show role selection if role fetch is complete AND no role exists
  if (!role && !roleLoading) return (
    <RoleSelectionScreen
      onSelect={async (selectedRole) => {
        try {
          await updateRole(selectedRole);
          // Force reload if needed, although state update should handle it
          // window.location.reload(); 
        } catch (error) { // Error already logged in hook
          await showAlert('Error al guardar el rol. Por favor, verifica tu conexión o intenta más tarde.\n' + (error.message || ''), { type: 'error' });
        }
      }}
    />
  );

  // SECURITY: Enforce email verification
  if (!user.emailVerified) {
    const resendVerification = async () => {
      try {
        const { sendEmailVerification } = await import('firebase/auth');
        await sendEmailVerification(user);
        await showAlert('Email de verificación enviado. Por favor, revisa tu bandeja de entrada y spam.', { type: 'success' });
      } catch (error) {
        console.error('Error sending verification email:', error);
        await showAlert('Error al enviar el email de verificación. Inténtalo de nuevo.', { type: 'error' });
      }
    };

    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-neutral-900 border-2 border-red-600 rounded-2xl p-8 space-y-6">
          <div className="flex justify-center">
            <AlertCircle size={64} className="text-red-500" />
          </div>
          <div className="text-center space-y-3">
            <h2 className="text-2xl font-black text-white uppercase tracking-tight">
              {t('auth.verify_email_title')}
            </h2>
            <p className="text-sm text-neutral-400">
              {t('auth.verify_email_sent_to')}
            </p>
            <p className="text-sm font-bold text-white break-all">
              {user.email}
            </p>
            <p className="text-xs text-neutral-500">
              {t('auth.verify_email_desc')}
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={resendVerification}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-black py-3 rounded-lg uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
            >
              <Mail size={18} />
              {t('auth.resend_email')}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-neutral-800 hover:bg-neutral-700 text-white font-bold py-3 rounded-lg uppercase tracking-wider transition-colors"
            >
              {t('auth.email_verified_btn')}
            </button>
            <button
              onClick={handleLogout}
              className="w-full bg-transparent border border-neutral-700 hover:border-red-600 text-neutral-400 hover:text-white font-bold py-3 rounded-lg uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
            >
              <LogOut size={18} />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // /app is an explicit route to enter the app - falls through to Layout below
  return (
    <SyncQueueProvider>
      <LiveAnnouncerProvider>
        <TourProvider>
          <SkipLink />
          <Layout
            user={user}
            profile={profile}
            role={role}
            activeTab={activeTab}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            isProfileComplete={isProfileComplete}
          >
            <SEOHead />
            {/* <UpdatePrompt /> */}
            <ErrorModal />

            {/* Pending Deletion Banner */}
            {pendingDeletion && (
              <div className="fixed top-0 left-0 md:left-64 right-0 bg-red-950/95 border-b-2 border-red-600 z-[100] animate-in slide-in-from-top">
                <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertCircle size={20} className="text-red-500 animate-pulse" />
                    <p className="font-bold text-white">
                      {t('auth.account_deletion_warning')}{' '}
                      <span className="text-red-400">
                        {new Date(pendingDeletion.executionAt).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </p>
                  </div>
                  <button
                    onClick={reactivateAccount}
                    className="px-4 py-2 bg-white hover:bg-neutral-200 text-black font-black text-xs uppercase tracking-wider rounded-lg transition-colors"
                  >
                    {t('auth.cancel_deletion')}
                  </button>
                </div>
              </div>
            )}

            <ErrorBoundary>
              {renderContent()}
            </ErrorBoundary>

            {/* PWA Indicators */}
            <OfflineIndicator />
            <SyncIndicator />
          </Layout>
        </TourProvider>
      </LiveAnnouncerProvider>
    </SyncQueueProvider>
  );
}
